import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BellOffIcon, CameraOffIcon, CheckIcon, CircleAlertIcon, ClockIcon, DownloadIcon, ImageOffIcon, LockIcon, MapPinOffIcon, SearchXIcon, WifiOffIcon, WrenchIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SkeletonCards, SkeletonPost } from '../components/Skeleton';
import { StateScreen } from '../components/StateScreen';
import { useLocale } from '../contexts/LocaleContext';
import { homePath } from '../utils/clientReview';
type Config = {
  tag?: string;
  icon: React.ReactNode;
  tone?: 'light' | 'dark';
  points?: string[];
  primary?: string;
  secondary?: string;
  action?: 'home' | 'retry' | 'settings' | 'check' | 'feed';
};
export function GlobalState() {
  const {
    kind = 'offline'
  } = useParams();
  const {
    t
  } = useLocale();
  const navigate = useNavigate();
  if (kind === 'loading' || kind === 'skeleton') {
    return <div className="px-2 pb-2" data-id="element-3047" data-mp="12sw494883onq">
        <PageHeader title={t(`state.${kind}.title`)} subtitle={t(`state.${kind}.body`)} showBack data-id="element-3048" data-mp="3r80863050mmr" />
        <SkeletonCards count={4} data-id="element-3049" data-mp="1pdqppmuk4e" />
        <div className="mt-2" data-id="element-3050" data-mp="50a4zzmwptgp">
          <SkeletonPost data-id="element-3051" data-mp="500nz1juawn5" />
        </div>
      </div>;
  }
  const configs: Record<string, Config> = {
    offline: {
      icon: <WifiOffIcon size={26} data-id="element-3052" data-mp="93f5xxrc9n38" />,
      points: [t('state.offline.p1'), t('state.offline.p2'), t('state.offline.p3')],
      primary: t('state.retry'),
      action: 'retry'
    },
    error: {
      icon: <CircleAlertIcon size={26} data-id="element-3053" data-mp="iyguoshpcn5a" />,
      primary: t('state.retry'),
      secondary: t('state.backHome'),
      action: 'retry'
    },
    empty: {
      icon: <SearchXIcon size={26} data-id="element-3054" data-mp="splafuf7cc16" />,
      primary: t('state.backHome'),
      action: 'home'
    },
    success: {
      icon: <CheckIcon size={26} data-id="element-3055" data-mp="wrg6ajed5x7k" />,
      tone: 'dark',
      primary: t('state.backHome'),
      action: 'home'
    },
    locked: {
      icon: <LockIcon size={26} data-id="element-3056" data-mp="11pzwivzzpv5q" />,
      primary: t('create.gate.cta'),
      action: 'check'
    },
    session: {
      icon: <ClockIcon size={26} data-id="element-3057" data-mp="2xsw6s7jy2jyr" />,
      tone: 'dark',
      primary: t('state.session.cta'),
      action: 'home'
    },
    maintenance: {
      icon: <WrenchIcon size={26} data-id="element-3058" data-mp="2ttx6x1d2lde6" />,
      tone: 'dark',
      primary: t('state.retry'),
      action: 'retry'
    },
    update: {
      icon: <DownloadIcon size={26} data-id="element-3059" data-mp="37lk1mwrd6duu" />,
      primary: t('state.update.cta'),
      secondary: t('state.update.later'),
      action: 'home'
    },
    camera: {
      icon: <CameraOffIcon size={26} data-id="element-3060" data-mp="3hgc1k78f5css" />,
      primary: t('state.permission.cta'),
      secondary: t('state.permission.skip'),
      action: 'home'
    },
    notifications: {
      icon: <BellOffIcon size={26} data-id="element-3061" data-mp="3qilc2x3ot666" />,
      primary: t('state.permission.cta'),
      secondary: t('state.permission.skip'),
      action: 'home'
    },
    location: {
      icon: <MapPinOffIcon size={26} data-id="element-3062" data-mp="1w4c0ckc0lduw" />,
      primary: t('state.permission.cta'),
      secondary: t('state.permission.skip'),
      action: 'home'
    },
    photos: {
      icon: <ImageOffIcon size={26} data-id="element-3063" data-mp="1r723ijmapzx1" />,
      primary: t('state.permission.cta'),
      secondary: t('state.permission.skip'),
      action: 'home'
    },
    'no-results': {
      icon: <SearchXIcon size={26} data-id="element-3064" data-mp="bl86xgscqg6b" />,
      primary: t('state.backHome'),
      action: 'home'
    },
    'no-notifications': {
      icon: <BellOffIcon size={26} data-id="element-3065" data-mp="3tz7mm5h3ujoj" />,
      primary: t('state.backHome'),
      action: 'home'
    },
    'no-posts': {
      icon: <ImageOffIcon size={26} data-id="element-3066" data-mp="1vezmrz8sn7v" />,
      primary: t('nav.new'),
      action: 'feed'
    },
    'no-metrics': {
      icon: <CircleAlertIcon size={26} data-id="element-3067" data-mp="3k11o7xuibvof" />,
      primary: t('metrics.editBody'),
      action: 'home'
    },
    'first-use': {
      icon: <CheckIcon size={26} data-id="element-3068" data-mp="3p1z423s1odmr" />,
      tone: 'dark',
      primary: t('home.firstUse.cta'),
      action: 'check'
    }
  };
  const config = configs[kind] ?? configs.error;
  const run = (action?: Config['action']) => {
    switch (action) {
      case 'check':
        return navigate('/check');
      case 'feed':
        return navigate('/novo');
      case 'settings':
        return navigate('/config');
      case 'retry':
        return navigate('/estados');
      default:
        return navigate(homePath());
    }
  };
  return <StateScreen tag={t(`state.${kind}.tag`)} title={t(`state.${kind}.title`)} body={t(`state.${kind}.body`)} points={config.points} icon={config.icon} tone={config.tone} primary={config.primary ? {
    label: config.primary,
    onClick: () => run(config.action)
  } : undefined} secondary={config.secondary ? {
    label: config.secondary,
    onClick: () => navigate('/estados')
  } : undefined} data-id="element-3069" data-mp="oiwi6s4bpipu" />;
}